import { createVirtualizer, defaultRangeExtractor } from '@tanstack/solid-virtual'
import { createEffect, createSignal, For, on, onMount, Show } from 'solid-js'
import { createViewportHeight } from '../lib/create-viewport-height.ts'
import { ProductionWorkCard } from './production-work-card.tsx'
import type { ProductionWorkListProps } from './production-work-list.tsx'

export const ProductionWorkVirtualList = (
  props: ProductionWorkListProps & { footer: () => HTMLElement },
) => {
  let viewport!: HTMLElement
  const availableHeight = createViewportHeight(
    () => viewport,
    () => props.footer(),
  )
  const [focusedId, setFocusedId] = createSignal<string>()
  const virtualizer = createVirtualizer<HTMLElement, HTMLLIElement>({
    get count() {
      return props.tasks.length
    },
    getScrollElement: () => viewport,
    estimateSize: () => (props.touch ? 180 : 110),
    getItemKey: (index) => props.tasks[index]?.id ?? index,
    overscan: 4,
    get rangeExtractor() {
      const focusedIndex = props.tasks.findIndex((task) => task.id === focusedId())
      return (range: Parameters<typeof defaultRangeExtractor>[0]) => {
        const indexes = defaultRangeExtractor(range)
        if (focusedIndex >= 0 && !indexes.includes(focusedIndex)) indexes.push(focusedIndex)
        return indexes.sort((left, right) => left - right)
      }
    },
  })
  createEffect(
    on(
      () => [props.view, props.search, props.equipmentId, props.workCenterId],
      () => {
        setFocusedId(undefined)
        virtualizer.scrollToOffset(0)
      },
    ),
  )
  createEffect(() => {
    const last = virtualizer.getVirtualItems().at(-1)
    if (
      last &&
      last.index >= props.tasks.length - 5 &&
      props.hasMore &&
      !props.fetching &&
      !props.error
    )
      props.onLoadMore()
  })
  return (
    <section
      ref={viewport}
      aria-label="Прокручування завдань"
      aria-busy={props.fetching}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: The bounded viewport must support keyboard scrolling.
      tabIndex={0}
      class="min-w-0 overflow-y-auto overscroll-contain rounded-control focus-visible:outline-2 focus-visible:outline-accent"
      style={{
        height: `max(180px, calc(${availableHeight()}px - var(--spacing-gutter) - var(--spacing) * 2))`,
      }}
    >
      <ul
        aria-label="Виробничі завдання"
        class="relative w-full"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        <For each={virtualizer.getVirtualItems()}>
          {(row) => (
            <Show when={props.tasks[row.index]}>
              {(task) => (
                <li
                  aria-setsize={props.total ?? -1}
                  aria-posinset={row.index + 1}
                  data-index={row.index}
                  ref={(element) => onMount(() => virtualizer.measureElement(element))}
                  class="absolute top-0 left-0 w-full"
                  style={{ transform: `translateY(${row.start}px)` }}
                  onFocusIn={() => setFocusedId(task().id)}
                  onFocusOut={(event) => {
                    if (
                      !(event.relatedTarget instanceof Node) ||
                      !event.currentTarget.contains(event.relatedTarget)
                    )
                      setFocusedId(undefined)
                  }}
                >
                  <ProductionWorkCard
                    task={task()}
                    touch={props.touch}
                    onOpen={() => props.onOpen(task().id)}
                  />
                </li>
              )}
            </Show>
          )}
        </For>
      </ul>
    </section>
  )
}
